import { useState, useEffect } from "react";
import FormInput from "../../../components/controls/FormInput";
import FormSelectBox from "../../../components/controls/FormSelectBox";
import FormTextarea from "../../../components/controls/FormTextArea";
import ImageUploader from "../../../components/controls/ImageUploader";
import SearchableSelectBox from "../../../components/controls/SearchableSelectBox";
import {
  EyeIcon,
  EyeSlashIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import Button from "../../../components/controls/Button";
import DateTimePicker from "../../../components/controls/DateTimePicker";

export default function AdminNewsModal({ item, onClose, onSaved, userId }) {
  const [title, setTitle] = useState(item.title);
  const [content, setContent] = useState(item.text);
  const [imageUrl, setImageUrl] = useState(item.imageUrl);
  const [publishDate, setPublishDate] = useState(
    item.publishDate ? new Date(item.publishDate) : null
  );
  const [tagId, setTagId] = useState(item.tagId);
  const [tags, setTags] = useState([]);

  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(item.companyId);
  const [hiddenByAdmin, setHiddenByAdmin] = useState(item.isHiddenByAdmin);

  const [isFeatured, setIsFeatured] = useState(item.isFeatured || false);
  const [featuredStartAt, setFeaturedStartAt] = useState(
    item.featuredStartAt ? item.featuredStartAt.substring(0, 16) : ""
  );
  const [featuredEndAt, setFeaturedEndAt] = useState(
    item.featuredEndAt ? item.featuredEndAt.substring(0, 16) : ""
  );

  const isHiddenByAdmin = item.isHiddenByAdmin;
  const isAccepted = item.status === "accepted";

  useEffect(() => {
    loadTags();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Company/admin-list`,
      {
        headers: { "X-User-Id": userId },
      }
    );

    if (!res.ok) return;

    const data = await res.json();
    setCompanies(
      data.map((x) => ({
        id: x.id,
        name: x.name,
      }))
    );
  };

  const toggleAdminHidden = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/${item.id}/toggle-admin-hidden`,
      {
        method: "POST",
        headers: { "X-User-Id": userId },
      }
    );

    if (!res.ok) {
      alert("Erreur lors du changement de visibilité.");
      return;
    }

    setHiddenByAdmin(!hiddenByAdmin);
  };

  const loadTags = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/Tags`,
      {
        headers: { "X-User-Id": userId },
      }
    );

    if (!res.ok) return;

    const data = await res.json();
    setTags(data.map((x) => ({ id: x.id, name: x.nameFr })));
  };

  const save = async () => {
    const payload = {
      id: item.id,
      title,
      content,
      imageUrl,
      publishDate: publishDate ? new Date(publishDate) : null,
      tagId,
      companyId: Number(companyId),
      isFeatured,
      featuredStartAt: featuredStartAt ? new Date(featuredStartAt) : null,
      featuredEndAt: featuredEndAt ? new Date(featuredEndAt) : null,
    };

    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/admin-review`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
        },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) return alert("Erreur lors de l'enregistrement.");
    onSaved();
  };

  const approve = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/${item.id}/approve`,
      {
        method: "POST",
        headers: { "X-User-Id": userId },
      }
    );

    if (!res.ok) return alert("Erreur lors de l'approbation.");

    setShowStatusMenu(false); // ← Ferme le menu immédiatement
    onSaved(); // ← Recharge la liste + ferme le modal
  };

  const reject = async () => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/News/${item.id}/reject`,
      {
        method: "POST",
        headers: { "X-User-Id": userId },
      }
    );

    if (!res.ok) return alert("Erreur lors du refus.");

    setShowStatusMenu(false);
    onSaved();
  };

  return (
    <dialog className="modal modal-open modal-middle">
      <div className="modal-box relative max-w-5xl max-h-[90vh] overflow-y-auto p-6 bg-white text-gray-800">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-xl">Modifier l’actualité</h3>

          <div className="flex items-center gap-4">
            {/* -------- Icône de visibilité -------- */}
            <button
              onClick={toggleAdminHidden}
              className={`p-2 rounded-full border transition ${
                hiddenByAdmin
                  ? "border-red-400 bg-red-50 text-red-600 hover:bg-red-100"
                  : "border-green-400 bg-green-50 text-green-600 hover:bg-green-100"
              }`}
            >
              {hiddenByAdmin ? (
                <EyeSlashIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>

            {/* -------- Changer statut -------- */}
            {!isAccepted && (
              <div className="relative">
                <Button
                  as="button"
                  type="button"
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  size="md"
                  className="w-full px-4 py-2 font-semibold"
                  rightIcon={<ChevronDownIcon className="w-4 h-4" />}
                >
                  Changer le statut
                </Button>

                {showStatusMenu && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border p-2 z-50">
                    <button
                      className="w-full text-left px-4 py-2 rounded-lg hover:bg-gray-100 text-gray-900"
                      onClick={approve}
                    >
                      Accepter
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 rounded-lg hover:bg-red-50 text-red-600"
                      onClick={reject}
                    >
                      Refuser
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* -------- X pour fermer -------- */}
            <button
              className="text-2xl text-gray-500 hover:text-black px-2 py-1"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>

        {item.isFeatured && (
          <div className="alert alert-info mb-4">
            <span>
              En vedette du {new Date(item.featuredStartAt).toLocaleString()} au{" "}
              {new Date(item.featuredEndAt).toLocaleString()}
            </span>
          </div>
        )}

        {hiddenByAdmin && (
          <div className="alert alert-error mb-4 shadow-md">
            <span>Cet article est caché par un modérateur.</span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormInput
            label="Titre"
            value={title}
            onChange={(n, v) => setTitle(v)}
            required
          />

          <DateTimePicker
            label="Date de publication"
            value={publishDate}
            onChange={setPublishDate}
          />

          <FormSelectBox
            label="Tag"
            value={tagId}
            onChange={(n, v) => setTagId(v)}
            options={tags}
            placeholder="Sélectionner un tag"
            required
          />

          <SearchableSelectBox
            label="Compagnie"
            value={companyId}
            onChange={(n, v) => setCompanyId(parseInt(v, 10))}
            options={companies}
            placeholder="Sélectionner une compagnie"
          />

          <div className="p-4 border rounded-xl  mb-4">
            <div className="flex items-center gap-3 mb-4">
              <input
                type="checkbox"
                className="checkbox checkbox-primary border-[#FFD76A]"
                checked={isFeatured}
                onChange={() => setIsFeatured(!isFeatured)}
              />
              <span className="font-semibold text-lg">En vedette</span>
            </div>

            {isFeatured && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DateTimePicker
                  label="Début"
                  value={featuredStartAt}
                  onChange={setFeaturedStartAt}
                />

                <DateTimePicker
                  label="Fin"
                  value={featuredEndAt}
                  onChange={setFeaturedEndAt}
                />
              </div>
            )}
          </div>
        </div>

        <FormTextarea
          label="Texte de l'article"
          value={content}
          onChange={(n, v) => setContent(v)}
          lines={12}
          required
          maxLength={10000}
          showMaxLength
        />

        <div className="my-4">
          <ImageUploader
            initialImageUrl={imageUrl}
            onTempImageChange={(url) => setImageUrl(url || "")}
            bucket="actualites"
          />
        </div>

        <div className="modal-action mt-6">
          <button className="btn btn-outline rounded-full" onClick={onClose}>
            Annuler
          </button>

          <button
            className="btn bg-primary rounded-full text-white"
            onClick={save}
          >
            Enregistrer
          </button>
        </div>
      </div>
    </dialog>
  );
}
